import React, { useMemo } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Paths from "routes/Paths";
import { getPostSignInPath } from "routes/signInRedirect";

import { VerifyEmailRoute } from "./routes/AccountRoutes";
import ContactRoute from "./routes/ContactRoute";
import ExploreRoute from "./routes/ExploreRoute";
import HelpSupportRoute from "./routes/HelpSupportRoute";
import HomeRoute from "./routes/HomeRoute";
import {
	CookiePolicyRoute,
	PrivacyPolicyRoute,
	RefundPolicyRoute,
	RightsPolicyRoute,
	TermsOfServiceRoute
} from "./routes/LegalRoutes";
import NewsletterUnsubscribeRoute from "./routes/NewsletterUnsubscribeRoute";
import NotFoundRoute from "./routes/NotFoundRoute";
import NotificationsRoute from "./routes/NotificationsRoute";
import PlansRoute from "./routes/PlansRoute";
import SettingsRoute from "./routes/SettingsRoute";
import SharedWithMeRoute from "./routes/SharedWithMeRoute";
import TreeEditorRoute from "./routes/TreeEditorRoute";
import TreeFullViewRoute from "./routes/TreeFullViewRoute";
import TreeViewRoute from "./routes/TreeViewRoute";
import UserProfileRoute from "./routes/UserProfileRoute";

const PrivateRouter = () => {
	// A signed-in visit to /signin honours the ?redirect param (e.g. a shared tree link opened
	// while already logged in) instead of always landing on the homepage.
	const { search } = useLocation();
	const signInRedirectElement = useMemo(() => <Navigate to={getPostSignInPath(search)} replace />, [search]);
	const homeRouteElement = useMemo(() => <HomeRoute />, []);
	const exploreRouteElement = useMemo(() => <ExploreRoute />, []);
	const notificationsRouteElement = useMemo(() => <NotificationsRoute />, []);
	const sharedWithMeRouteElement = useMemo(() => <SharedWithMeRoute />, []);
	const plansRouteElement = useMemo(() => <PlansRoute />, []);
	const settingsRouteElement = useMemo(() => <SettingsRoute />, []);
	const treeViewRouteElement = useMemo(() => <TreeViewRoute />, []);
	const treeEditorRouteElement = useMemo(() => <TreeEditorRoute />, []);
	const treeFullViewRouteElement = useMemo(() => <TreeFullViewRoute />, []);
	const userProfileRouteElement = useMemo(() => <UserProfileRoute />, []);
	const verifyEmailRouteElement = useMemo(() => <VerifyEmailRoute />, []);
	const helpSupportRouteElement = useMemo(() => <HelpSupportRoute />, []);
	const contactRouteElement = useMemo(() => <ContactRoute />, []);
	const privacyPolicyRouteElement = useMemo(() => <PrivacyPolicyRoute />, []);
	const termsOfServiceRouteElement = useMemo(() => <TermsOfServiceRoute />, []);
	const refundPolicyRouteElement = useMemo(() => <RefundPolicyRoute />, []);
	const cookiePolicyRouteElement = useMemo(() => <CookiePolicyRoute />, []);
	const rightsPolicyRouteElement = useMemo(() => <RightsPolicyRoute />, []);
	const newsletterUnsubscribeRouteElement = useMemo(() => <NewsletterUnsubscribeRoute />, []);
	const notFoundRouteElement = useMemo(() => <NotFoundRoute />, []);

	return (
		<Routes>
			<Route element={signInRedirectElement} path={Paths.signIn} />
			<Route element={homeRouteElement} path={Paths.root} />
			<Route element={exploreRouteElement} path={Paths.explore} />
			<Route element={notificationsRouteElement} path={Paths.notifications} />
			<Route element={sharedWithMeRouteElement} path={Paths.sharedWithMe} />
			<Route element={plansRouteElement} path={Paths.plans} />
			<Route element={settingsRouteElement} path={Paths.settings} />
			<Route element={treeViewRouteElement} path={Paths.treeViewPattern} />
			<Route element={treeEditorRouteElement} path={Paths.treeEditPattern} />
			<Route element={treeFullViewRouteElement} path={Paths.treeFullViewPattern} />
			<Route element={userProfileRouteElement} path={Paths.userProfilePattern} />
			<Route element={verifyEmailRouteElement} path={Paths.verifyEmail} />
			<Route element={helpSupportRouteElement} path={Paths.helpSupport} />
			<Route element={contactRouteElement} path={Paths.contact} />
			<Route element={privacyPolicyRouteElement} path={Paths.privacyPolicy} />
			<Route element={termsOfServiceRouteElement} path={Paths.termsOfService} />
			<Route element={refundPolicyRouteElement} path={Paths.refundPolicy} />
			<Route element={cookiePolicyRouteElement} path={Paths.cookiePolicy} />
			<Route element={rightsPolicyRouteElement} path={Paths.rightsPolicy} />
			<Route element={newsletterUnsubscribeRouteElement} path={Paths.newsletterUnsubscribe} />
			<Route element={notFoundRouteElement} path={Paths.notFound} />
		</Routes>
	);
};

export default PrivateRouter;
